import { createRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { Modal } from "@/components/application/modals/modal-controlled";
import { useToast } from "@/components/application/notifications/toast-provider";
import { apiCall, ApiError } from "../../lib/apiClient";
import { useTenantList } from "../../hooks/useTenantQueries";

/**
 * Detail střediska pro global admina: uživatelé tenantu + členové mikroskupin
 * + tankovací karty s držiteli a samostatným přiřazením (mimo rozpočet).
 *
 * Atribuce je per FuelCard → holder (NE per SPZ). Přiřazení mění držitele pro
 * budoucí rozpočtové drafty; schválené rozpočty jsou immutable snapshoty.
 */

interface BillingGroupRow {
  id: string;
  code: string;
  display_name: string;
  tenant_id: string;
  deactivated_at: string | null;
  zone_id: string | null;
  zone_code: string | null;
  client_zone_url: string | null;
  zone_has_credentials: boolean;
  payment_account_id: string | null;
  payment_account_label: string | null;
  default_holder_id: string | null;
  default_holder_label: string | null;
}

interface HolderSummary {
  id: string;
  holder_type: "legal" | "physical";
  display_name: string;
}

interface TenantUser {
  user_id: string;
  display_name: string;
  email: string;
  role: string;
}

interface MicrogroupMember {
  member_id: string;
  user_id: string;
  display_name: string;
  email: string;
  microgroup_id: string;
  status: string;
}

interface DetailCard {
  fuel_card_id: string;
  card_label: string | null;
  card_number_display: string | null;
  holder_id: string | null;
  effective_holder_id: string | null;
  effective_holder_label: string | null;
  holder_is_default: boolean;
  status: string;
}

interface BillingGroupDetailResponse {
  billing_group: BillingGroupRow;
  holders: HolderSummary[];
  tenant_users: TenantUser[];
  microgroup_members: MicrogroupMember[];
  cards: DetailCard[];
}

function BillingGroupDetailPage() {
  const { id } = Route.useParams();
  const [detail, setDetail] = useState<BillingGroupDetailResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // id karty, u které právě probíhá uložení přiřazení držitele.
  const [assigningCardId, setAssigningCardId] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [assignTenantOpen, setAssignTenantOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiCall<BillingGroupDetailResponse>(
        `/api/admin/billing-groups/${id}/detail`,
      );
      setDetail(data);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení detailu střediska selhalo.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const assignHolder = useCallback(
    async (fuelCardId: string, holderId: string | null) => {
      setAssigningCardId(fuelCardId);
      setLoadError(null);
      try {
        await apiCall(`/api/admin/billing-groups/${id}/assign-card-holder`, {
          method: "POST",
          body: JSON.stringify({
            holder_assignments: { [fuelCardId]: holderId },
          }),
        });
        await fetchDetail();
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : "Přiřazení držitele karty selhalo.");
      } finally {
        setAssigningCardId(null);
      }
    },
    [id, fetchDetail],
  );

  const setDefaultHolder = useCallback(
    async (holderId: string | null) => {
      setSavingDefault(true);
      setLoadError(null);
      try {
        await apiCall(`/api/admin/billing-groups/${id}/default-holder`, {
          method: "PUT",
          body: JSON.stringify({ default_holder_id: holderId }),
        });
        await fetchDetail();
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : "Nastavení default držitele selhalo.");
      } finally {
        setSavingDefault(false);
      }
    },
    [id, fetchDetail],
  );

  // Možnosti držitele = aktivní holdeři registru tenantu střediska.
  const holderOptions = useMemo<HolderSummary[]>(
    () => detail?.holders ?? [],
    [detail],
  );

  const userColumns = useMemo<Column<TenantUser>[]>(
    () => [
      { key: "name", header: "Jméno", cell: (r) => <strong className="text-primary">{r.display_name}</strong> },
      { key: "email", header: "E-mail", cell: (r) => r.email },
      { key: "role", header: "Role", cell: (r) => <Badge color="gray">{r.role}</Badge> },
    ],
    [],
  );

  const memberColumns = useMemo<Column<MicrogroupMember>[]>(
    () => [
      { key: "name", header: "Jméno", cell: (r) => <strong className="text-primary">{r.display_name}</strong> },
      { key: "email", header: "E-mail", cell: (r) => r.email },
      {
        key: "status",
        header: "Stav",
        cell: (r) => (
          <BadgeWithDot color={r.status === "active" ? "success" : "gray"}>
            {r.status === "active" ? "Aktivní" : r.status}
          </BadgeWithDot>
        ),
      },
    ],
    [],
  );

  const cardColumns = useMemo<Column<DetailCard>[]>(
    () => [
      {
        key: "card",
        header: "Karta",
        cell: (r) => (
          <div className="flex flex-col gap-0.5">
            <strong className="text-primary">{r.card_label ?? "—"}</strong>
            {r.card_number_display && (
              <span className="text-xs text-tertiary">{r.card_number_display}</span>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) => (
          <BadgeWithDot color={r.status === "active" ? "success" : "gray"}>
            {r.status === "active" ? "Aktivní" : r.status}
          </BadgeWithDot>
        ),
      },
      {
        key: "holder",
        header: "Držitel",
        cell: (r) => (
          <div className="flex flex-col gap-1">
            <select
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              value={r.holder_id ?? ""}
              disabled={assigningCardId === r.fuel_card_id}
              onChange={(e) =>
                void assignHolder(r.fuel_card_id, e.target.value === "" ? null : e.target.value)
              }
              aria-label={`Držitel karty ${r.card_label ?? r.fuel_card_id}`}
              data-testid={`card-holder-${r.fuel_card_id}`}
            >
              <option value="">
                {r.holder_is_default
                  ? `— default: ${r.effective_holder_label ?? "středisko"} —`
                  : "— bez držitele —"}
              </option>
              {holderOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.display_name}
                  {o.holder_type === "legal" ? " (PO)" : ""}
                </option>
              ))}
            </select>
            {r.holder_is_default && (
              <span className="text-xs text-tertiary">dědí default držitele střediska</span>
            )}
          </div>
        ),
      },
    ],
    [assignHolder, assigningCardId, holderOptions],
  );

  const bg = detail?.billing_group;

  return (
    <section aria-labelledby="bg-detail-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-2">
        <Link to="/admin/billing-groups" className="text-sm text-brand-secondary hover:underline w-fit">
          ← Zpět na střediska
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="bg-detail-title" className="text-2xl font-bold text-primary m-0">
            {bg ? `${bg.code} — ${bg.display_name}` : "Detail střediska"}
          </h2>
          {bg &&
            (bg.zone_id ? (
              <BadgeWithDot color={bg.zone_has_credentials ? "success" : "warning"}>
                {bg.zone_code ?? "Zóna"}
              </BadgeWithDot>
            ) : (
              <BadgeWithDot color="warning">
                Bez klientské zóny
              </BadgeWithDot>
            ))}
          {bg && (
            <Button
              size="sm"
              color="secondary"
              onPress={() => setAssignTenantOpen(true)}
              data-testid="assign-tenant-open"
            >
              Přiřadit tenanta
            </Button>
          )}
        </div>
      </header>

      {bg && assignTenantOpen && (
        <AssignTenantModal
          billingGroupId={bg.id}
          currentTenantId={bg.tenant_id}
          onClose={() => setAssignTenantOpen(false)}
          onSaved={() => {
            setAssignTenantOpen(false);
            void fetchDetail();
          }}
        />
      )}

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Uživatelé tenantu
            {detail && (
              <span className="ml-2 text-sm font-normal text-tertiary">
                ({detail.tenant_users.length})
              </span>
            )}
          </h3>
        </CardHeader>
        <CardBody>
          <DataTable
            caption="Aktivní uživatelé tenantu střediska"
            columns={userColumns}
            rows={detail?.tenant_users ?? []}
            rowKey={(r) => r.user_id}
            loading={loading}
            emptyMessage="Žádní uživatelé tenantu."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Členové mikroskupin tenantu
            {detail && (
              <span className="ml-2 text-sm font-normal text-tertiary">
                ({detail.microgroup_members.length})
              </span>
            )}
          </h3>
        </CardHeader>
        <CardBody>
          <DataTable
            caption="Aktivní členové mikroskupin tenantu"
            columns={memberColumns}
            rows={detail?.microgroup_members ?? []}
            rowKey={(r) => r.member_id}
            loading={loading}
            emptyMessage="Žádní členové mikroskupin."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Tankovací karty
            {detail && (
              <span className="ml-2 text-sm font-normal text-tertiary">
                ({detail.cards.length})
              </span>
            )}
          </h3>
        </CardHeader>
        <CardBody>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label htmlFor="default-holder" className="text-sm text-tertiary">
              Default držitel střediska:
            </label>
            <select
              id="default-holder"
              className="block w-full max-w-xs min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              value={bg?.default_holder_id ?? ""}
              disabled={savingDefault}
              onChange={(e) => void setDefaultHolder(e.target.value === "" ? null : e.target.value)}
              data-testid="default-holder-select"
            >
              <option value="">— bez default držitele —</option>
              {holderOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.display_name}
                  {o.holder_type === "legal" ? " (PO)" : ""}
                </option>
              ))}
            </select>
            <span className="text-xs text-tertiary">
              aplikuje se na karty bez vlastního přiřazení (typicky firma pro celé středisko)
            </span>
          </div>
          <DataTable
            caption="Tankovací karty střediska s přiřazením držitele"
            columns={cardColumns}
            rows={detail?.cards ?? []}
            rowKey={(r) => r.fuel_card_id}
            loading={loading}
            emptyMessage="Žádné karty pro toto středisko."
          />
        </CardBody>
      </Card>
    </section>
  );
}

interface AssignTenantModalProps {
  billingGroupId: string;
  currentTenantId: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * UC-005 Model A — přeřazení Střediska pod jiný tenant. Backend přerazítkuje
 * VŠECHNA data Střediska (princip #6) v jedné transakci. Sdílený držitel napříč
 * středisky → 409 BILLING_GROUP_SHARED_HOLDER (fail-fast).
 */
function AssignTenantModal({ billingGroupId, currentTenantId, onClose, onSaved }: AssignTenantModalProps) {
  const tenants = useTenantList();
  const [tenantId, setTenantId] = useState(currentTenantId);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  async function submit() {
    if (!tenantId || tenantId === currentTenantId) {
      setFormError("Vyberte jiného tenanta.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      await apiCall(`/api/admin/billing-groups/${billingGroupId}/tenant`, {
        method: "PUT",
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      toast.push({ variant: "success", message: "Středisko přeřazeno pod nového tenanta." });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === "BILLING_GROUP_SHARED_HOLDER") {
        setFormError(
          "Přeřazení nelze provést: některý držitel je atribuovaný i na karty jiného střediska. Nejprve atribuci rozdělte.",
        );
      } else if (e instanceof ApiError && e.errorCode === "BILLING_GROUP_CONSENT_BLOCKED") {
        setFormError(
          "Přeřazení nelze provést: uživatel navázaný na držitele má udělený souhlas a nelze ho automaticky přesunout. Vyřešte ručně.",
        );
      } else {
        setFormError(e instanceof Error ? e.message : "Přeřazení selhalo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      title="Přiřadit Středisko tenantovi"
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => void submit()}
            isDisabled={submitting}
            isLoading={submitting}
            data-testid="assign-tenant-save"
          >
            Přeřadit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <InlineAlert variant="error">{formError}</InlineAlert>}
        <InlineAlert variant="warning">
          Přeřazení přesune VŠECHNA data střediska (karty, transakce, rozpočty, vyúčtování)
          pod nového tenanta. Akce je nevratná tlačítkem — zpět jen opětovným přeřazením.
        </InlineAlert>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="assign-tenant-select" className="text-sm font-medium text-secondary">
            Cílový tenant <span className="text-brand-tertiary">*</span>
          </label>
          <select
            id="assign-tenant-select"
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            disabled={submitting}
            data-testid="assign-tenant-select"
          >
            {(tenants.data?.rows ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/billing-groups/$id",
  component: BillingGroupDetailPage,
});
